import React, { useEffect, useState } from "react";
import AddOwnerView from "./AddOwnerView";
import { GetUserOwnerDTO } from "../../../models/user/user.response";
import { userService } from "../../../services/userService";
import { TelephoneIcon, ShopLocalIcon, CalendarGreyIcon, EditIcon, DeleteRedIcon, PlusThinWhiteIcon, FilterIcon } from "../../../assets/icons";
import { useNotification } from "../../toster-notification/NotificationContext";
import Loader from "../../shared/Loader";
import { Switch } from "@mui/material";
import { confirmDialog } from "../../../utils/confirmDialog";

const OwnersPage: React.FC = () => {

    const [isAddOwnerOpen, setIsAddOwnerOpen] = useState(false);
    const [owners, setOwners] = useState<GetUserOwnerDTO[]>([]);
    const [filterText, setFilterText] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    const { notify } = useNotification();

    useEffect(() => {
        fetchOwners();
    }, []);

    const fetchOwners = async () => {
        setLoading(true);
        try {
            const response = await userService.getOwners();

            if (response.success && response.data) {
                setOwners(response.data);
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to fetch owners.");
            }
            console.error("Failed to fetch owners:", error);
        } finally {
            setLoading(false);
        }
    }

    const handleDelete = async (id: string) => {
        setLoading(true);
        try {
            const response = await userService.delete(id);

            if (response.success) {
                notify("success", response.message);
                fetchOwners();
            } else {
                notify("error", response.message);
            }

        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to delete owner.");
            }
            console.error("Failed to delete owner:", error);
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteHard = async (id: string) => {

        const confirmed = await confirmDialog({
            title: "Obriši vlasnika?",
            description: "Ova akcija je nepovratna. Svi podaci vlasnika će biti trajno obrisani.",
            isDelete: true
        });

        if (!confirmed) return;

        setLoading(true);

        try {
            const response = await userService.deleteHard(id);
            if (response.success) {
                notify("success", response.message);
                fetchOwners();
            } else {
                notify("error", response.message);
            }

        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to delete hard owner.");
            }
            console.error("Failed to delete hard owner:", error);
        } finally {
            setLoading(false);
        }
    }

    const handleRestore = async (id: string) => {
        setLoading(true);
        try {
            const response = await userService.restore(id);

            if (response.success) {
                notify("success", response.message);
                fetchOwners();
            } else {
                notify("error", response.message);
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to restore owner.");
            }
            console.error("Failed to restore owner:", error);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {isAddOwnerOpen &&
                <AddOwnerView
                    onClose={() => {
                        setIsAddOwnerOpen(false);
                        fetchOwners();
                    }}
                />
            }

            <section className="sa-owners-section">
                <div className="w-layout-blockcontainer container w-container">
                    <div className="sttings-content-wrapper">
                        <div className="header-div">
                            <div>
                                <h2 className="heading-3">Upravljanje Vlasnicima</h2>
                                <div className="text-block">
                                    Pregled i upravljanje nalozima vlasnika lokala<br />
                                </div>
                            </div>
                            <div className="header-button-div">
                                <div className="button-primary " onClick={() => setIsAddOwnerOpen(true)}>
                                    <img loading="lazy" src={PlusThinWhiteIcon} alt="" className="button-image" />
                                    <div className="button-text">Dodaj vlasnika</div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-settings-box-div table-block-div">
                            <div className="list-header">
                                <div className="list-header-info">
                                    <div className="list-header-title">Vlasnici Lokala</div>
                                    <div className="list-header-description">
                                        Ukupno {owners.length} vlasnika u sistemu<br />
                                    </div>
                                </div>
                                <div className="list-header-filer">
                                    <img loading="lazy" src={FilterIcon} alt="" className="subcategory-header-filter-image" />
                                    <div className="list-header-filer-form w-form">
                                        <form className="form">
                                            <input className="form-input w-input" placeholder="Pretrazi vlasnike..." type="text"
                                                value={filterText}
                                                onChange={(e) => setFilterText(e.target.value)}
                                            />
                                        </form>
                                    </div>
                                </div>
                            </div>
                            <div className="list-div">
                                <div className="list-row-div list-header-row owners">
                                    <div>Vlasnik</div>
                                    <div>Kontakt</div>
                                    <div>Kompanija</div>
                                    <div>Lokacije</div>
                                    <div>Status</div>
                                    <div>Poslednja prijava</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076d99-1cee91cf">Akcije</div>
                                </div>

                                {owners
                                    .filter(owner =>
                                        owner.fullName.toLowerCase().includes(filterText.toLowerCase()) ||
                                        owner.email.toLowerCase().includes(filterText.toLowerCase()))
                                    .map((owner) => (
                                        <div className="list-row-div owners">
                                            <div className="sa-owner-info-div">
                                                <div className="sa-owner-name">{owner.fullName}</div>
                                                <div className="description-text">{owner.email}</div>
                                            </div>
                                            <div className="div-block-10">
                                                <img loading="lazy" src={TelephoneIcon} alt="" className="icon-size-20" />
                                                <div className="description-text">{owner.phone}</div>
                                            </div>
                                            <div className="div-block-10">
                                                <img loading="lazy" src={ShopLocalIcon} alt="" className="icon-size-20" />
                                                {owner.companyNames
                                                .slice(0, 1)
                                                .map((name, index) => (
                                                    <div key={index} className="sa-owner-company-name">{name} {index === owner.companyNames.length-1 ? "" : ","} {owner.companyNames.length > 1 && index === 0 ? `+${owner.companyNames.length - 1}` : ""}</div>
                                                ))}
                                            </div>
                                            <div className="div-block-2 location">
                                                <div>{owner.locations}</div>
                                            </div>
                                            <Switch
                                                checked={owner.isActive}
                                                onChange={() => { owner.isActive ? handleDelete(owner.id) : handleRestore(owner.id); }}
                                            />
                                            <div className="description-text">{new Date(owner.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.')}</div>
                                            <div id="w-node-d4546c81-6fa0-6f88-d6ea-0c2d58bd5b3f-1cee91cf" className="table-row-action-btns-div">
                                                <img loading="lazy" src={EditIcon} alt="" className="category-list-div-row-actions-image" />
                                                <img loading="lazy" src={DeleteRedIcon} alt="" className="category-list-div-row-actions-image" onClick={() => handleDeleteHard(owner.id)} />
                                            </div>
                                        </div>
                                    ))}

                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default OwnersPage;